import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Avatar,
  Snackbar,
  Alert,
  Checkbox,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import HistoryIcon from '@mui/icons-material/History'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { reportsService, organizationService } from '../services'
import useAuthStore from '../store/authStore'
import { canExportData } from '../utils/adminPermissions'
import { formatOrganizationLabel } from '../utils/organizationLabel'
import {
  DEFAULT_ROWS,
  ORG_SEARCH_DEBOUNCE_MS,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/SalesReport/constants'
import {
  fmtDate,
  fmtAmount,
  fmtSaleDateTime,
  getSaleOrderListTimestamp,
  getDefaultRange,
  reportTabFromPath,
} from '../components/SalesReport/formatters'
import SalesReport from '../components/SalesReport/SalesReport'
import ArticleCodeCell from '../components/SalesReport/ArticleCodeCell'

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />

const ReportsPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const showCompiledExcelExport = canExportData(adminPermissions)
  const location = useLocation()
  const navigate = useNavigate()
  const [tabValue, setTabValue] = useState(() => reportTabFromPath(location.pathname))

  useEffect(() => {
    setTabValue(reportTabFromPath(location.pathname))
  }, [location.pathname])
  const [salesSubTab, setSalesSubTab] = useState(0)
  const [selectedOrgs, setSelectedOrgs] = useState([])
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [sortBy, setSortBy] = useState('dateModified')
  const [sortOrder, setSortOrder] = useState('DESC')
  const [organizations, setOrganizations] = useState([])
  const [orgsLoading, setOrgsLoading] = useState(true)
  const [orgSearchText, setOrgSearchText] = useState('')
  const [orgInputValue, setOrgInputValue] = useState('')
  const [dateRange, setDateRange] = useState(getDefaultRange())
  const [lotSource, setLotSource] = useState('all')
  /** all | inventory | non_inventory → maps to negativeOnly query param */
  const [salesType, setSalesType] = useState('all')
  const [exportingTable, setExportingTable] = useState(false)
  const [exportingCompiled, setExportingCompiled] = useState(false)
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' })
  const [salesPaginationResetKey, setSalesPaginationResetKey] = useState(0)

  const [stockState, setStockState] = useState({
    items: [],
    pagination: { total: 0, page: 1, limit: DEFAULT_ROWS },
    loading: false,
    totalCostValue: 0,
    totalRetailValue: 0,
  })
  const [lineItemsState, setLineItemsState] = useState({
    open: false,
    inventoryId: null,
    productName: '',
    items: [],
    loading: false,
    pagination: { total: 0, page: 1, limit: DEFAULT_ROWS },
  })
  const [invoiceDetail, setInvoiceDetail] = useState({
    open: false,
    loading: false,
    orderId: null,
    invoice: null,
    items: [],
  })

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const getErrorMessage = (error, fallback) => {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      fallback
    return typeof message === 'string' ? message : fallback
  }

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setOrgsLoading(true)
        const { items: orgItems } = await organizationService.getOrganizationsPage({
          page: 1,
          limit: 20,
          search: orgSearchText.trim() || undefined,
        })
        if (!cancelled) {
          setOrganizations(Array.isArray(orgItems) ? orgItems : [])
        }
      } catch (error) {
        console.error('Error fetching organizations:', error)
        if (!cancelled) setOrganizations([])
      } finally {
        if (!cancelled) setOrgsLoading(false)
      }
    }, ORG_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [orgSearchText])

  const orgOptions = useMemo(() => {
    const fromSearch = organizations.map((org) => ({
      id: String(org.id),
      label: formatOrganizationLabel(org),
    }))
    const selectedMissing = selectedOrgs.filter(
      (selected) => !fromSearch.some((option) => option.id === selected.id),
    )
    return [...selectedMissing, ...fromSearch]
  }, [organizations, selectedOrgs])

  const selectedOrgIdsCsv = useMemo(
    () =>
      selectedOrgs.length > 0
        ? selectedOrgs.map((org) => org.id).join(',')
        : undefined,
    [selectedOrgs],
  )

  // undefined = All, true = Non Inventory, false = Inventory
  const negativeOnly =
    salesType === 'non_inventory' ? true : salesType === 'inventory' ? false : undefined

  const resetAllPagesToFirst = () => {
    setSalesPaginationResetKey((k) => k + 1)
    setStockState((s) =>
      s.pagination.page === 1 ? s : { ...s, pagination: { ...s.pagination, page: 1 } },
    )
  }

  const salesFilterParams = useMemo(
    () => ({
      orgIds: selectedOrgIdsCsv,
      search: search || undefined,
      sortBy,
      sortOrder,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      lotSource: lotSource === 'all' ? undefined : lotSource,
      ...(negativeOnly !== undefined ? { negativeOnly } : {}),
    }),
    [
      selectedOrgIdsCsv,
      search,
      sortBy,
      sortOrder,
      dateRange.startDate,
      dateRange.endDate,
      lotSource,
      negativeOnly,
    ],
  )

  const summaryFilterParams = useMemo(
    () => ({
      orgIds: selectedOrgIdsCsv,
      search: search || undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      lotSource: lotSource === 'all' ? undefined : lotSource,
      ...(negativeOnly !== undefined ? { negativeOnly } : {}),
    }),
    [
      selectedOrgIdsCsv,
      search,
      dateRange.startDate,
      dateRange.endDate,
      lotSource,
      negativeOnly,
    ],
  )

  const dateError =
    dateRange.startDate && dateRange.endDate && dateRange.startDate > dateRange.endDate
      ? 'Start date cannot be after end date'
      : null

  const handleSalesTableError = useCallback((message) => {
    setToast({ open: true, severity: 'error', message })
  }, [])

  const handleSalesSortChange = useCallback(
    (field) => {
      setSalesPaginationResetKey((k) => k + 1)
      if (sortBy === field) {
        setSortOrder((p) => (p === 'ASC' ? 'DESC' : 'ASC'))
      } else {
        setSortBy(field)
        setSortOrder('DESC')
      }
    },
    [sortBy],
  )

  useEffect(() => {
    if (tabValue !== 1) return undefined

    let cancelled = false
    const run = async () => {
      setStockState((s) => ({ ...s, loading: true }))
      try {
        const res = await reportsService.getInventoryStockReport({
          orgIds: selectedOrgIdsCsv,
          search: search || undefined,
          sortBy: sortBy || undefined,
          sortOrder,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          page: stockState.pagination.page,
          limit: stockState.pagination.limit,
        })
        if (!cancelled) {
          setStockState((s) => ({
            ...s,
            items: res.items || [],
            pagination: res.pagination || s.pagination,
            totalCostValue: Number(res.summary?.totalCostValue) || 0,
            totalRetailValue: Number(res.summary?.totalRetailValue) || 0,
            loading: false,
          }))
        }
      } catch (error) {
        console.error('Failed to load inventory stock:', error)
        if (!cancelled) {
          setStockState((s) => ({
            ...s,
            loading: false,
            items: [],
            totalCostValue: 0,
            totalRetailValue: 0,
          }))
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    tabValue,
    selectedOrgIdsCsv,
    search,
    sortBy,
    sortOrder,
    dateRange.startDate,
    dateRange.endDate,
    stockState.pagination.page,
    stockState.pagination.limit,
  ])

  const fetchLineItems = async (inventoryId, productName, page = 1, limit = DEFAULT_ROWS) => {
    setLineItemsState((s) => ({
      ...s,
      open: true,
      inventoryId,
      productName,
      loading: true,
    }))
    try {
      const res = await reportsService.getInventoryStockLineItems(inventoryId, {
        page,
        limit,
      })
      setLineItemsState((s) => ({
        ...s,
        items: res.items || [],
        pagination: res.pagination || s.pagination,
        loading: false,
      }))
    } catch (error) {
      console.error('Failed to load inventory line items:', error)
      setLineItemsState((s) => ({ ...s, items: [], loading: false }))
    }
  }

  const openInvoiceDetail = async (orderId) => {
    setInvoiceDetail({ open: true, loading: true, orderId, invoice: null, items: [] })
    try {
      const res = await reportsService.getSalesInvoiceItems(orderId)
      setInvoiceDetail({
        open: true,
        loading: false,
        orderId,
        invoice: res.invoice || null,
        items: res.items || [],
      })
    } catch (err) {
      console.error('Failed to load invoice detail:', err)
      setInvoiceDetail({ open: true, loading: false, orderId, invoice: null, items: [] })
    }
  }

  const exportCurrentSalesTable = async () => {
    if (exportingTable || exportingCompiled || dateError) return
    try {
      setExportingTable(true)
      const table = salesSubTab === 0 ? 'invoices' : 'products'
      const response = await reportsService.exportSalesTable({
        table,
        orgIds: selectedOrgIdsCsv,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        search: search || undefined,
        sortBy: sortBy || undefined,
        sortOrder,
        lotSource: lotSource === 'all' ? undefined : lotSource,
        ...(negativeOnly !== undefined ? { negativeOnly } : {}),
      })
      const filename = `sales-${table}-${new Date().toISOString().split('T')[0]}.csv`
      downloadBlob(response.data, filename)
      setToast({
        open: true,
        severity: 'success',
        message: 'Current table exported successfully.',
      })
    } catch (err) {
      console.error('Failed to export table:', err)
      setToast({
        open: true,
        severity: 'error',
        message: getErrorMessage(err, 'Could not export current table. Please try again.'),
      })
    } finally {
      setExportingTable(false)
    }
  }

  const exportCompiledSales = async (format = 'xlsx') => {
    if (exportingTable || exportingCompiled || dateError) return
    try {
      setExportingCompiled(true)
      const response = await reportsService.exportSalesCompiled({
        orgIds: selectedOrgIdsCsv,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        search: search || undefined,
        sortBy: sortBy || undefined,
        sortOrder,
        format,
        lotSource: lotSource === 'all' ? undefined : lotSource,
        ...(negativeOnly !== undefined ? { negativeOnly } : {}),
      })
      navigate('/reports/sales/exports', {
        state: {
          queuedMessage:
            response?.message ||
            'Export started. It will appear in Sales Compiled Exports when the file is ready.',
        },
      })
    } catch (err) {
      console.error('Failed to export compiled sales:', err)
      setToast({
        open: true,
        severity: 'error',
        message: getErrorMessage(err, 'Could not export compiled sales. Please try again.'),
      })
    } finally {
      setExportingCompiled(false)
    }
  }

  const renderTableHeaderCell = (label, field) => (
    <TableCell
      onClick={() => {
        resetAllPagesToFirst()
        if (sortBy === field) {
          setSortOrder((p) => (p === 'ASC' ? 'DESC' : 'ASC'))
        } else {
          setSortBy(field)
          setSortOrder('DESC')
        }
      }}
      sx={{ cursor: 'pointer', fontWeight: 700 }}
    >
      {label}
      {sortBy === field ? ` (${sortOrder})` : ''}
    </TableCell>
  )

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        User Reports Dashboard
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              fullWidth
              loading={orgsLoading}
              options={orgOptions}
              filterOptions={(options) => options}
              inputValue={orgInputValue}
              value={selectedOrgs}
              onChange={(_, options) => {
                resetAllPagesToFirst()
                setSelectedOrgs(options || [])
                // Clear typed search after select/deselect; "reset" is ignored below
                // so async option updates do not wipe in-progress typing.
                setOrgInputValue('')
                setOrgSearchText('')
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  setOrgInputValue(value || '')
                  setOrgSearchText(value || '')
                } else if (reason === 'clear') {
                  resetAllPagesToFirst()
                  setSelectedOrgs([])
                  setOrgInputValue('')
                  setOrgSearchText('')
                }
                // Ignore "reset" so async option updates do not overwrite typed text.
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.label || ''}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props
                return (
                  <li key={key} {...rest}>
                    <Checkbox
                      icon={checkboxIcon}
                      checkedIcon={checkboxCheckedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Shop"
                  placeholder={
                    selectedOrgs.length === 0
                      ? 'All shops — search to filter'
                      : 'Search shop'
                  }
                />
              )}
              size="small"
            />
            <TextField
              fullWidth
              label="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={
                selectedOrgs.length === 0
                  ? 'Search in all shops'
                  : 'Search in selected shops'
              }
              size="small"
            />
            <Button
              variant="contained"
              onClick={() => {
                resetAllPagesToFirst()
                setSearch(searchInput.trim())
              }}
              sx={{ minWidth: 100, height: 40, bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' }, fontSize: 13 }}
            >
              Apply
            </Button>
            <Button
              variant="text"
              onClick={() => {
                resetAllPagesToFirst()
                setSelectedOrgs([])
                setOrgInputValue('')
                setOrgSearchText('')
                setSearchInput('')
                setSearch('')
                setSortBy('')
                setSortOrder('DESC')
                setDateRange(getDefaultRange())
                setLotSource('all')
                setSalesType('all')
              }}
              sx={{ minWidth: 88, height: 40, fontSize: 13 }}
            >
              Reset
            </Button>
            </Stack>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            {tabValue === 0 ? (
              <>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Lot Source</InputLabel>
                <Select
                  value={lotSource}
                  label="Lot Source"
                  onChange={(e) => {
                    resetAllPagesToFirst()
                    setLotSource(e.target.value)
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="shwapno">Shwapno</MenuItem>
                  <MenuItem value="non_shwapno">Non-Shwapno</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Sales Type</InputLabel>
                <Select
                  value={salesType}
                  label="Sales Type"
                  onChange={(e) => {
                    resetAllPagesToFirst()
                    setSalesType(e.target.value)
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="inventory">Inventory Sales</MenuItem>
                  <MenuItem value="non_inventory">Non Inventory Sales</MenuItem>
                </Select>
              </FormControl>
              </>
            ) : null}
            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel>Sort On</InputLabel>
              <Select
                value={sortBy}
                label="Sort On"
                onChange={(e) => {
                  resetAllPagesToFirst()
                  setSortBy(e.target.value)
                }}
              >
                <MenuItem value="dateModified">Date Modified</MenuItem>
                <MenuItem value="orderDate">Date Created</MenuItem>
                <MenuItem value="amount">Amount</MenuItem>
                <MenuItem value="orgName">Shop</MenuItem>
                <MenuItem value="productName">Product Name</MenuItem>
                <MenuItem value="totalQuantitySold">Quantity Sold</MenuItem>
                <MenuItem value="totalSalesAmount">Total Sales Amount</MenuItem>
                <MenuItem value="quantityOnHand">Quantity On Hand</MenuItem>
                <MenuItem value="createdAt">Created At</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => {
                resetAllPagesToFirst()
                setSortOrder((p) => (p === 'ASC' ? 'DESC' : 'ASC'))
              }}
              sx={{ minWidth: 100, height: 40, fontSize: 13 }}
            >
              {sortOrder}
            </Button>
            <TextField
              label="Start Date"
              type="date"
              value={dateRange.startDate}
              onChange={(e) => {
                resetAllPagesToFirst()
                setDateRange((d) => ({ ...d, startDate: e.target.value }))
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 170 }}
              error={!!dateError}
            />
            <TextField
              label="End Date"
              type="date"
              value={dateRange.endDate}
              onChange={(e) => {
                resetAllPagesToFirst()
                setDateRange((d) => ({ ...d, endDate: e.target.value }))
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 170 }}
              error={!!dateError}
              helperText={dateError || ' '}
            />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {tabValue === 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={exportingTable ? <CircularProgress color="inherit" size={16} /> : <DownloadIcon />}
            onClick={exportCurrentSalesTable}
            disabled={exportingTable || exportingCompiled}
            sx={{ height: 40, fontSize: 13 }}
          >
            {exportingTable ? 'Exporting Current Table...' : 'Export Current Table'}
          </Button>
          {showCompiledExcelExport ? (
            <>
              <Button
                variant="contained"
                startIcon={exportingCompiled ? <CircularProgress color="inherit" size={16} /> : <DownloadIcon />}
                onClick={() => exportCompiledSales('xlsx')}
                disabled={exportingTable || exportingCompiled}
                sx={{ height: 40, fontSize: 13 }}
              >
                {exportingCompiled ? 'Starting Export...' : 'Export Compiled Excel'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => navigate('/reports/sales/exports')}
                sx={{ height: 40, fontSize: 13 }}
              >
                Sales Compiled Exports
              </Button>
            </>
          ) : null}
        </Stack>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Sales Report" />
            <Tab label="Inventory Stock" />
          </Tabs>
        </Box>

        {tabValue === 0 ? (
          <SalesReport
            enabled={tabValue === 0 && !dateError}
            salesFilterParams={salesFilterParams}
            summaryFilterParams={summaryFilterParams}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSalesSortChange}
            onOpenInvoice={openInvoiceDetail}
            salesSubTab={salesSubTab}
            onSalesSubTabChange={setSalesSubTab}
            resetPaginationKey={salesPaginationResetKey}
            onError={handleSalesTableError}
          />
        ) : (
        <Box sx={{ p: 2 }}>
            <>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                Total records: {stockState.pagination?.total || 0}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(180deg, #FFFFFF 0%, #E8F4FC 100%)',
                    px: 2,
                    py: 1.75,
                    minHeight: 76,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px -1px rgba(0,0,0,0.06), 0 4px 6px -1px rgba(0,0,0,0.1)',
                  }}
                >
                  {stockState.loading ? (
                    <CircularProgress size={22} sx={{ color: '#11809A' }} />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#25314C', mb: 0.5 }}>
                      {fmtAmount(stockState.totalCostValue)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Total stock cost
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF4E5 100%)',
                    px: 2,
                    py: 1.75,
                    minHeight: 76,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px -1px rgba(0,0,0,0.06), 0 4px 6px -1px rgba(0,0,0,0.1)',
                  }}
                >
                  {stockState.loading ? (
                    <CircularProgress size={22} sx={{ color: '#11809A' }} />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#25314C', mb: 0.5 }}>
                      {fmtAmount(stockState.totalRetailValue)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Total retail value
                  </Typography>
                </Box>
              </Stack>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      {renderTableHeaderCell('Article Code', 'articleCode')}
                      <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                      {renderTableHeaderCell('Inventory ID', 'inventoryId')}
                      {renderTableHeaderCell('Shop', 'orgName')}
                      {renderTableHeaderCell('Product', 'productName')}
                      {renderTableHeaderCell('Qty', 'quantityOnHand')}
                      {renderTableHeaderCell('Selling Price', 'unitCost')}
                      {renderTableHeaderCell('MRP', 'unitPrice')}
                      <TableCell sx={{ fontWeight: 700 }}>Non-Shwapno</TableCell>
                      {renderTableHeaderCell('Updated', 'dateModified')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockState.loading ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      stockState.items.map((row) => (
                        <TableRow
                          key={row.inventoryId}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() =>
                            fetchLineItems(row.inventoryId, row.productName)
                          }
                        >
                          <TableCell><ArticleCodeCell articleCode={row.articleCode} /></TableCell>
                          <TableCell>
                            <Avatar
                              variant="rounded"
                              src={row.imageThumbnail || undefined}
                              alt={row.productName || 'Product'}
                              sx={{ width: 32, height: 32, bgcolor: '#f3f4f6', fontSize: 12 }}
                            />
                          </TableCell>
                          <TableCell>{row.inventoryId}</TableCell>
                          <TableCell>{row.orgName}</TableCell>
                          <TableCell>{row.productName}</TableCell>
                          <TableCell>{row.quantityOnHand}</TableCell>
                          <TableCell>{fmtAmount(row.unitCost)}</TableCell>
                          <TableCell>{fmtAmount(row.unitPrice)}</TableCell>
                          <TableCell>{row.isNonShwapno ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{fmtDate(row.dateModified)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={stockState.pagination?.total || 0}
                rowsPerPage={stockState.pagination?.limit || DEFAULT_ROWS}
                page={(stockState.pagination?.page || 1) - 1}
                onPageChange={(_, nextPage) => {
                  setStockState((s) => ({
                    ...s,
                    pagination: { ...s.pagination, page: nextPage + 1 },
                  }))
                }}
                onRowsPerPageChange={(e) => {
                  const next = parseInt(e.target.value, 10)
                  setStockState((s) => ({
                    ...s,
                    pagination: { ...s.pagination, page: 1, limit: next },
                  }))
                }}
              />
            </>
        </Box>
        )}
      </Card>

      <Dialog
        open={lineItemsState.open}
        onClose={() => setLineItemsState((s) => ({ ...s, open: false }))}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Inventory Line Items: {lineItemsState.productName} (Inventory #{lineItemsState.inventoryId})
        </DialogTitle>
        <DialogContent>
          {lineItemsState.loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      <TableCell>Article Code</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Stock Qty</TableCell>
                      <TableCell>Total Stock</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItemsState.items.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell><ArticleCodeCell articleCode={row.articleCode} /></TableCell>
                        <TableCell>
                          <Avatar
                            variant="rounded"
                            src={row.imageThumbnail || undefined}
                            alt={row.productName || 'Product'}
                            sx={{ width: 32, height: 32, bgcolor: '#f3f4f6', fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.productName}</TableCell>
                        <TableCell>{row.stockQty}</TableCell>
                        <TableCell>{row.totalStock}</TableCell>
                        <TableCell>{row.expiryDate || 'N/A'}</TableCell>
                        <TableCell>{row.orderId || 'N/A'}</TableCell>
                        <TableCell>{fmtDate(row.createdAt)}</TableCell>
                        <TableCell>{fmtDate(row.updatedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={lineItemsState.pagination?.total || 0}
                rowsPerPage={lineItemsState.pagination?.limit || DEFAULT_ROWS}
                page={(lineItemsState.pagination?.page || 1) - 1}
                onPageChange={(_, nextPage) =>
                  fetchLineItems(
                    lineItemsState.inventoryId,
                    lineItemsState.productName,
                    nextPage + 1,
                    lineItemsState.pagination?.limit || DEFAULT_ROWS,
                  )
                }
                onRowsPerPageChange={(e) =>
                  fetchLineItems(
                    lineItemsState.inventoryId,
                    lineItemsState.productName,
                    1,
                    parseInt(e.target.value, 10),
                  )
                }
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={invoiceDetail.open}
        onClose={() => setInvoiceDetail((s) => ({ ...s, open: false }))}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Invoice #{invoiceDetail.orderId} Items
        </DialogTitle>
        <DialogContent>
          {invoiceDetail.loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                {invoiceDetail.invoice?.organizationName || 'N/A'} - {fmtSaleDateTime(getSaleOrderListTimestamp(invoiceDetail.invoice))}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Invoice Total: {fmtAmount(invoiceDetail.invoice?.amount || 0)}
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Article Code</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>MRP</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Line Total</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Non-Shwapno</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceDetail.items.map((item) => (
                      <TableRow key={item.lineItemId}>
                        <TableCell><ArticleCodeCell articleCode={item.articleCode} /></TableCell>
                        <TableCell>
                          <Avatar
                            variant="rounded"
                            src={item.imageThumbnail || undefined}
                            alt={item.productName || 'Product'}
                            sx={{ width: 32, height: 32, bgcolor: '#f3f4f6', fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{fmtAmount(item.mrp)}</TableCell>
                        <TableCell>{fmtAmount(item.totalPrice)}</TableCell>
                        <TableCell>{item.isNonShwapno ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default ReportsPage
